import { useEffect } from 'react';
import {
  App,
  Button,
  Form,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, CloudOutlined } from '@ant-design/icons';
import { useAccessibleMdms } from '@/hooks/useMdms';
import { useAssignMdm, useRemoveMdm, useUser } from '@/hooks/useUsers';
import type { UserDto, UserMdmAssignmentDto } from '@/types/user';

const { Text } = Typography;

interface Props {
  user: UserDto | null;
  onClose: () => void;
}

export function UserMdmModal({ user, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ mdmId: number; roleId: number }>();

  const { data: fullUser, refetch } = useUser(user?.id ?? null);
  const { data: allMdms } = useAccessibleMdms();

  const assignMdm = useAssignMdm();
  const removeMdm = useRemoveMdm();

  useEffect(() => {
    if (user) {
      form.resetFields();
      refetch();
    }
  }, [user, form, refetch]);

  const handleAssign = async () => {
    try {
      const values = await form.validateFields();
      await assignMdm.mutateAsync({
        userId: user!.id,
        mdmId: values.mdmId,
      });
      message.success('MDM asignado.');
      form.resetFields();
      refetch();
    } catch {
      // error manejado por interceptor
    }
  };

  const handleRemove = async (mdmId: number) => {
    try {
      await removeMdm.mutateAsync({ userId: user!.id, mdmId });
      message.success('MDM removido.');
      refetch();
    } catch {
      // error manejado por interceptor
    }
  };

  const assignedMdmIds = fullUser?.mdms?.map((m) => m.mdmId) ?? [];
  const availableMdms = (allMdms ?? []).filter((m) => !assignedMdmIds.includes(m.id));

  return (
    <Modal
      open={user != null}
      title={
        <Space>
          <CloudOutlined />
          {`MDMs de ${user?.firstName} ${user?.lastName}`}
        </Space>
      }
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={560}
      destroyOnHidden
    >
      {/* MDMs asignados */}
      <List
        size="small"
        bordered
        style={{ marginBottom: 16 }}
        locale={{ emptyText: 'Sin MDMs asignados' }}
        dataSource={fullUser?.mdms ?? [] as UserMdmAssignmentDto[]}
        renderItem={(item: UserMdmAssignmentDto) => (
          <List.Item
            actions={[
              <Popconfirm
                key="remove"
                title="¿Remover este MDM?"
                onConfirm={() => handleRemove(item.mdmId)}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  loading={removeMdm.isPending}
                />
              </Popconfirm>,
            ]}
          >
            <Space>
              <CloudOutlined style={{ color: 'var(--color-primary)' }} />
              <Text strong>{item.mdmName}</Text>
              <Tag>{item.mdmCode}</Tag>
            </Space>
          </List.Item>
        )}
      />

      {/* Asignar nuevo MDM */}
      {availableMdms.length > 0 && (
        <Form form={form} layout="inline">
          <Form.Item
            name="mdmId"
            rules={[{ required: true, message: 'Seleccioná un MDM' }]}
            style={{ flex: 1 }}
          >
            <Select
              placeholder="Agregar MDM..."
              options={availableMdms.map((m) => ({
                value: m.id,
                label: `${m.name} (${m.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAssign}
              loading={assignMdm.isPending}
            >
              Agregar
            </Button>
          </Form.Item>
        </Form>
      )}

      {availableMdms.length === 0 && (assignedMdmIds.length > 0) && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          El usuario ya tiene acceso a todos los MDMs disponibles.
        </Text>
      )}
    </Modal>
  );
}
